import type { SupabaseClient } from "@supabase/supabase-js";

import { accountFullySetUp } from "@/lib/account-setup";
import { supabase } from "@/lib/supabase";
import { getAnonSupabase } from "@/lib/supabase-anon";
import { UserCacheService } from "@/lib/user-cache";
import type { Database } from "@/types/database";

/**
 * What the SIGNED-OUT VIEW may read straight from the database.
 *
 * Owner decision (2026-10-13): emails and rights-holder information are for FULLY SET-UP signed-in
 * accounts only (`src/lib/account-setup.ts`). Everyone else gets the signed-out view: signed-out
 * visitors, blocked accounts and unfinished sign-ups. The anon key ships in every page, so the rule
 * lives in the database:
 *   - backend/supabase/migrations/20261013060000_anon_privacy.sql grants `anon` these columns and
 *     no others. A signed-out request for any other column (`select("*")` and `tracks(*)` included)
 *     fails with "permission denied for table".
 *   - backend/supabase/migrations/20261013055000_set_up_accounts_only.sql lets a signed-in account
 *     that is not fully set up read NO `tracks` rows through its own session.
 * So the signed-out view reads through the anon-only client (`supabase-anon.ts`) with these lists,
 * whoever is signed in: `publicReadForViewer()` decides.
 *
 * Keep the lists in lockstep with 20261013060000 and with `PUBLIC_TRACK_COLUMNS` in
 * backend/core/services/public_track_view.py (the backend's own public routes use the same list).
 * `lyrics` is not among them: the lyrics are withheld from the signed-out view too.
 *
 * A fully set-up account still asks for `*` through its own session, and sees exactly what it saw
 * before.
 */
export const PUBLIC_TRACK_COLUMNS = [
  "id",
  "title",
  "artist",
  "album",
  "duration",
  "genre",
  "subgenre",
  "moods",
  "instruments",
  "tags",
  "bpm",
  "key",
  "tempo",
  "energy",
  "language",
  "vocal_type",
  "vocal_gender",
  "explicit_content",
  "description",
  "lyrical_theme",
  "sync_context",
  "emotional_arc",
  "cultural_fusion",
  "harmony",
  "chord_progression",
  "historical_period",
  "artwork_url",
  "cover_art_url",
  "isrc",
  "upc",
  "iswc",
  "release_date",
  "year",
  "label_tag",
  "is_published",
  "parent_track_id",
  "version_type",
  "edit_type",
  "licensing_tier",
  "pricing_method",
  "fixed_price",
  "min_price",
  "max_price",
  "sweet_spot_price",
  "allowed_usage_types",
  "licensing_restrictions",
  "worldwide",
  "territories_included",
  "territories_excluded",
  "created_at",
  "updated_at",
] as const;

/**
 * Every `playlists` column but the curator's search brief (`search_metadata`, `source_prompt`) and
 * the Disco import internals (`import_payload`, `import_error`, `source_url`).
 */
export const PUBLIC_PLAYLIST_COLUMNS = [
  "id",
  "name",
  "description",
  "user_id",
  "cover_image_url",
  "is_public",
  "created_at",
  "updated_at",
  "source",
  "organization_id",
  "import_status",
] as const;

/**
 * How a public page reads the catalog for the viewer at hand:
 *   full    a fully set-up account: its own session (`supabase`) and every column (`*`)
 *   public  everyone else: the anon-only client and the public columns above
 */
export interface PublicRead {
  client: SupabaseClient<Database>;
  /** For `select(...)` on `tracks`, or inside a `tracks (...)` embed. */
  trackColumns: string;
  /** For `select(...)` on `playlists`. */
  playlistColumns: string;
  view: "full" | "public";
}

function fullRead(): PublicRead {
  return { client: supabase, trackColumns: "*", playlistColumns: "*", view: "full" };
}

function publicRead(): PublicRead {
  return {
    client: getAnonSupabase(),
    trackColumns: PUBLIC_TRACK_COLUMNS.join(", "),
    playlistColumns: PUBLIC_PLAYLIST_COLUMNS.join(", "),
    view: "public",
  };
}

/**
 * Decide the read for the viewer at hand. Asked of the Supabase client's session, which is what the
 * database will see, not of React state: while the auth provider is still loading, the two can
 * disagree. With a session, the account's profile decides (`accountFullySetUp`); a profile that
 * can't be read gets the public view (fail closed): a set-up account then sees less for a moment,
 * never the other way round.
 */
export async function publicReadForViewer(): Promise<PublicRead> {
  const { data } = await supabase.auth.getSession();
  const session = data.session;
  const userId = session?.access_token ? session.user?.id : undefined;
  if (!userId) return publicRead();

  try {
    const profile = await UserCacheService.getProfile(userId);
    return accountFullySetUp(profile) ? fullRead() : publicRead();
  } catch {
    return publicRead();
  }
}
